import React, { useState, useEffect } from "react";
import "@/App.css";
import { toast } from "sonner";
import { buildPaidNotificationUrl } from "@/utils/whatsappOrder";
import { LanguageProvider } from "@/context/LanguageContext";
import Header from "@/components/Header";
import AnnouncementBar from "@/components/AnnouncementBar";
import { useAnnouncement, SeasonalBar, SeasonalPopup } from "@/components/Announcement";
import Hero from "@/components/Hero";
import TrustBand from "@/components/TrustBand";
import MarqueeBand from "@/components/MarqueeBand";
import ProductCatalog from "@/components/ProductCatalog";
import MasDelTzitzitHub from "@/components/MasDelTzitzitHub";
import CertificacionSection from "@/components/CertificacionSection";
import AboutUs from "@/components/AboutUs";
import Contact from "@/components/Contact";
import StoresSection from "@/components/StoresSection";
import FAQSection from "@/components/FAQSection";
import MaterialComparison from "@/components/MaterialComparison";
import Footer from "@/components/Footer";
import StickyCartMobile from "@/components/StickyCartMobile";
import SectionDivider from "@/components/SectionDivider";
import { OrderLookup } from "@/components/OrderTracking";
import LegalModal from "@/components/LegalModal";
import CartSidebar from "@/components/CartSidebar";
import SharedCartImporter from "@/components/SharedCartImporter";
import InstallBanner from "@/components/InstallBanner";
import AdminPanel from "@/components/AdminPanel";
import { Toaster } from "@/components/ui/sonner";
import { useCartPersistence } from "@/hooks/useCartPersistence";
import useScrollReveal from "@/hooks/useScrollReveal";
import { trackPageView } from "@/lib/analytics";

function App() {
  const [cartOpen, setCartOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const announcement = useAnnouncement();
  const [orderLookupOpen, setOrderLookupOpen] = useState(false);
  const [legalModal, setLegalModal] = useState(null);
  
  // Activate cart sync with backend (anonymous session)
  useCartPersistence();
  // Fade+slide reveal for sections as they scroll into view
  useScrollReveal();

  useEffect(() => {
    const onOrderLookup = () => setOrderLookupOpen(true);
    const onLegal = (e) => setLegalModal(e.detail);
    window.addEventListener('openOrderLookup', onOrderLookup);
    window.addEventListener('openLegal', onLegal);
    // Fire analytics page view on initial load
    trackPageView();
    return () => {
      window.removeEventListener('openOrderLookup', onOrderLookup);
      window.removeEventListener('openLegal', onLegal);
    };
  }, []);

  React.useEffect(() => {
    const handler = () => setAdminOpen(true);
    window.addEventListener('openAdmin', handler);
    return () => window.removeEventListener('openAdmin', handler);
  }, []);

  // Handle Mercado Pago return (back_urls). MP redirects to "/?mp_status=success&order_id=..."
  // We need to (a) confirm to user, (b) clear cart, (c) clean URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const mpStatus = params.get('mp_status');
    const orderId = params.get('order_id');
    if (!mpStatus) return;

    const cleanUrl = () => {
      const url = new URL(window.location.href);
      ['mp_status', 'order_id', 'collection_id', 'collection_status', 'payment_id',
       'status', 'external_reference', 'payment_type', 'merchant_order_id',
       'preference_id', 'site_id', 'processing_mode', 'merchant_account_id']
        .forEach(k => url.searchParams.delete(k));
      window.history.replaceState({}, '', url.pathname + (url.search ? url.search : ''));
    };

    if (mpStatus === 'success') {
      try {
        localStorage.removeItem('cart');
        window.dispatchEvent(new Event('cartUpdated'));
      } catch (e) {}
      toast.success('¡Pago aprobado!', {
        description: orderId ? `Tu pedido #${orderId.slice(0, 8)} fue registrado. Recibirás un correo de confirmación.` : 'Tu pedido fue registrado.',
        duration: 12000,
        action: {
          label: '📲 Avisar que ya pagué',
          onClick: () => window.open(buildPaidNotificationUrl(orderId, 'Mercado Pago'), '_blank'),
        },
      });
    } else if (mpStatus === 'pending') {
      toast.info('Pago pendiente', {
        description: 'Tu pago está siendo procesado por Mercado Pago. Te avisaremos cuando se confirme.',
        duration: 8000,
      });
    } else if (mpStatus === 'failure') {
      toast.error('Pago no completado', {
        description: 'No pudimos procesar tu pago. Tu carrito sigue intacto, puedes intentar de nuevo.',
        duration: 8000,
      });
    }
    cleanUrl();
  }, []);

  return (
    <LanguageProvider>
      <div className="App overflow-x-hidden">
        <SharedCartImporter onCartChanged={() => setCartOpen(true)} />
        {announcement && (announcement.type === 'bar' || announcement.type === 'both')
          ? <SeasonalBar ann={announcement} />
          : <AnnouncementBar />}
        <SeasonalPopup ann={announcement} />
        <Header onCartClick={() => setCartOpen(true)} />
        
        <main>
          <Hero />
          <TrustBand />
          <ProductCatalog />
          <MarqueeBand variant="kashrut" />
          <div className="reveal-on-scroll"><MaterialComparison /></div>
          <MarqueeBand variant="merits" />
          <div className="reveal-on-scroll"><MasDelTzitzitHub /></div>
          <SectionDivider variant="light" />
          <div className="reveal-on-scroll"><AboutUs /></div>
          <div className="reveal-on-scroll"><CertificacionSection /></div>
          <div className="reveal-on-scroll"><FAQSection /></div>
          <div className="reveal-on-scroll"><Contact /></div>
        </main>

        <MarqueeBand variant="footer" />
        <StoresSection />
        <Footer />

        <CartSidebar 
          isOpen={cartOpen}
          onClose={() => setCartOpen(false)}
        />

        <StickyCartMobile onCartClick={() => setCartOpen(true)} />

        <Toaster position="top-right" />
        <InstallBanner />
        {adminOpen && <AdminPanel onClose={() => setAdminOpen(false)} />}
        {orderLookupOpen && <OrderLookup onClose={() => setOrderLookupOpen(false)} />}
        <LegalModal open={!!legalModal} onOpenChange={(o) => !o && setLegalModal(null)} type={legalModal} />

        {/* WhatsApp floating button */}
        <a
          href="https://wa.me/525543233705?text=%C2%A1Hola%21%20Tengo%20una%20duda%20sobre%20los%20tzitziot%20de%20Mitzva%20Fit."
          target="_blank"
          rel="noopener noreferrer"
          data-testid="whatsapp-button"
          className="fixed bottom-6 right-6 z-50 bg-[#25D366] hover:bg-[#1ebe57] text-white rounded-full w-14 h-14 flex items-center justify-center shadow-lg hover:scale-110 transition-transform duration-200"
          aria-label="WhatsApp"
        >
          <svg viewBox="0 0 24 24" className="w-7 h-7 fill-white">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
        </a>
      </div>
    </LanguageProvider>
  );
}

export default App;
